import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import DocumentModalView from "./DocumentMod";

import { Link, useParams } from "react-router-dom";
import { fetchDocumentsByThreadId,fetchDocumentsByCase,updateFilledDataTemplate, uploadNewDocumentThunk } from "../../store/features/documentSlice";
import NotificationModal from "../../components/Notification/Notify";

const DocumentsView = ({ threadId }) => {
  const dispatch = useDispatch();
  const { documents, status: DocumentStatus, error } = useSelector((state) => state.documents);
  const { shipments, status: shipmentStatus } = useSelector((state) => state.shipments);
  const shipment = shipments.find((shipment) => shipment.thread_id === threadId);
  const { case_number,  emailBodies} = shipment;
  const [notification, setNotification] = useState({ isOpen: false, message: "" });
  const [documentType, setDocumentType] = useState("custom_clearance");
  const [sentBy, setSentBy] = useState("freight_forwarder");

  // Modal state
  const [isModalOpen, setModalOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [uploadingDocumentId, setUploadingDocumentId] = useState(null); // Track which document is being scanned
  const [isDocScanned, setDocScanned] = useState(false); // Track which document is being scanned
  const [isUploading, setIsUploading] = useState(false);

  // ✅ Initialize local state for temporary document updates
  const [localDocuments, setDocuments] = useState([]);

  // ✅ Sync Redux documents to local state on mount
  useEffect(() => {
    setDocuments(documents[case_number] || []);
  }, [documents, case_number]);


  const formatDate = (date) => {
    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, '0'); // Ensure two digits
    const month = String(d.getMonth() + 1).padStart(2, '0'); // Months are zero-based, so add 1
    const year = d.getFullYear();
  
    return `${day}-${month}-${year}`;
  };

  

  // Fetch documents when component mounts or threadId changes
  useEffect(() => {
    if (case_number) {
      dispatch(fetchDocumentsByCase(case_number));
    }
  }, [dispatch, case_number]);
 // Handle file selection and trigger upload
 const handleFileChange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;

      // ✅ Fetch the latest documents list for the case
      const existingDocuments = localDocuments;
      const lastId = existingDocuments.length > 0 
        ? Math.max(...existingDocuments.map(doc => doc.id)) 
        : 0;
      const newDocumentId = lastId + 1;

      // ✅ Initialize the new document object (temporary entry)
      const newDocument = {
        id: newDocumentId,
        name: file.name,
        type: "custom_clearance",
        sent_by: "freight_forwarder",
        last_updated: new Date().toLocaleString(),
        link: "", // Server will return actual link after upload
        ExtractedInfo: {},
        MandatoryInfo: {},
      };

      // ✅ Locally update the documents state to reflect the new file
      setDocuments([...localDocuments, newDocument]);

      // ✅ Prepare FormData for file upload
      const formData = new FormData();
      formData.append("file", file);
      formData.append("document_type", "custom_clearance");
      formData.append("sent_by", "freight_forwarder");

      try {
        setNotification({ isOpen: true, message: "Document uploaded successfully!" });
        setUploadingDocumentId(newDocumentId);
        setIsUploading(true);
        // ✅ Upload the document
        await dispatch(uploadNewDocumentThunk({ 
          caseNumber: case_number, 
          file, 
          documentType: "custom_clearance", 
          sentBy: "freight_forwarder" 
        }));
        
        // ✅ Fetch updated documents from server (replacing temporary entry)
        dispatch(fetchDocumentsByCase(case_number));
  
      } catch (error) {
        console.error("❌ Error handling document:", error);
        alert("Error processing the document. Please try again.");
        // ❌ Rollback temporary document from local state
        setDocuments(localDocuments.filter(doc => doc.id !== newDocumentId));
      } finally {
        setUploadingDocumentId(null);
        setIsUploading(false);
      }

};

// Handle button click to trigger file selection
const triggerFileUpload = () => {
  document.getElementById("file-upload").click();
};

  if (DocumentStatus === "loading") return <div>Loading documents...</div>;
  if (DocumentStatus === "failed") return <div>Failed to load documents</div>;

  // Handle opening modal
  const openModal = (doc) => {
    setSelectedDocument(doc);
    setModalOpen(true);
  };

  // Handle closing modal
  const closeModal = () => {
    setModalOpen(false);
    setSelectedDocument(null);
  };
  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h3 className="font-bold text-lg">List of Documents</h3>
        <div className="flex items-center">
          <input
            id="file-upload"
            type="file"
            className="hidden"
            onChange={handleFileChange}
          />
          <button
            onClick={triggerFileUpload}
            className="flex items-center gap-2 text-logo p-2 px-4 rounded-full border border-logo text-sm font-semibold hover:bg-logo hover:text-white transition duration-200 disabled:bg-gray-300 disabled:cursor-not-allowed"
            disabled={isUploading}
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="size-6">
              <path stroke-linecap="round" stroke-linejoin="round" d="M12 16.5V9.75m0 0 3 3m-3-3-3 3M6.75 19.5a4.5 4.5 0 0 1-1.41-8.775 5.25 5.25 0 0 1 10.233-2.33 3 3 0 0 1 3.758 3.848A3.752 3.752 0 0 1 18 19.5H6.75Z" />
            </svg>

            {isUploading ? "Uploading..." : "Upload File"}
          </button>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="table-auto w-full border-collapse border border-gray-300">
          <thead className="bg-gray-100">
            <tr>
              <th className="border border-gray-300 px-4 py-2 text-left">Document Name</th>
              <th className="border border-gray-300 px-4 py-2 text-left">Last Updated</th>
              <th className="border border-gray-300 px-4 py-2 text-left">Open Document</th>
            </tr>
          </thead>
          <tbody>
            {localDocuments.map((doc) => (
              <tr key={doc.id} className="hover:bg-gray-100">
                <td className="border border-gray-300 px-4 py-2">{doc.name}</td>
                <td className="border border-gray-300 px-4 py-2">{doc.last_updated}</td>
                <td className="border border-gray-300 px-4 py-2">
                  {uploadingDocumentId === doc.id ? (
                    <span className="text-gray-500">Scanning Document...</span>
                  ) : (
                    <button
                      onClick={() => openModal(doc)}
                      className="flex items-center text-font underline hover:text-logo"
                    >
                      View Document
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modal integration */}
      {isModalOpen && (
        <DocumentModalView
          isOpen={isModalOpen}
          documentId={selectedDocument.id}
          threadId={threadId}
          documentPath={selectedDocument.link}
          filledDataTemplate={selectedDocument.ExtractedInfo}
          onClose={closeModal}
          documentsData={localDocuments} // Pass all documents
          setSelectedDocument={setSelectedDocument} // Update document selection
        />
      )}
      {/* Notification Modal */}
      <NotificationModal
            isOpen={notification.isOpen}
            message={notification.message}
            onClose={() => setNotification({ isOpen: false, message: "" })}
      />
    </div>
    
  );
};

export default DocumentsView;
